import ActionButton from 'components/ActionButton';
import React, { useEffect, useMemo, useState } from 'react';
import { USER_ROLES, canItemBeSyncedInSalesChannel, isUserAuthorized, showError, showSuccess, stringifyAndParseObj } from 'utils';
import styles from './index.module.scss';
import DeleteActionForTable from 'components/DeleteActionForTableData';
import CustomLink from 'components/TableLink';
import DataTable from 'components/DataTable';
import { tableStyles } from 'css/tableStyles';
import { fetchSalesChannelMappingsForInventoryItem, removeInventoryItemFromSalesChannel, restoreInventoryItemToSalesChannel } from 'services/api/salesChannel';
import AddItemToSalesChannelModel from 'components/AddItemToSalesChannelModal';
import _ from 'lodash';
import { SyncItemButton } from 'pages/sales-channel/details/inventoryItems';

const ItemChannels = props => {
  const { userRoles } = props;
  const isUserAuthorizedToEdit = isUserAuthorized(userRoles, [USER_ROLES.PACKER, USER_ROLES.SUPERVISOR, USER_ROLES.MANAGER, USER_ROLES.ADMIN]);
  const [loading, setLoading] = useState<boolean>(false);
  const [restoreMappingLoading, setRestoreMappingLoading] = useState<boolean>(false);
  const [addSalesChannelModalOpen, setAddSalesChannelModalOpen] = useState<boolean>(false);
  const [salesChannels, setSalesChannels] = useState([]);

  const fetchSalesChannelMappings = async () => {
    try {
      setLoading(true);
      let response = await fetchSalesChannelMappingsForInventoryItem({ itemId: props.itemId });
      response = stringifyAndParseObj(response);
      setSalesChannels(response);
    } catch (e) {
      showError(e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchSalesChannelMappings();
  }, []);

  const getHeader = () => (
    <div className={styles['section-header']}>
      <div className={styles['section-heading']}>Sales Channels</div>
      <div className={'d-flex ml-auto'}>
        {isUserAuthorizedToEdit && <ActionButton
          text={'Add'}
          active={!loading}
          isLoading={loading}
          onClick={()=>{
            setAddSalesChannelModalOpen(true);
          }}
        />}
      </div>
    </div>
  );

  const deleteInventoryItem = async (row: any) => {
    try {
      await removeInventoryItemFromSalesChannel({
        salesChannelId: row?.salesChannel?.objectId,
        inventoryItemId: props?.itemId,
      });
      showSuccess('Item removed successfully.');
      await fetchSalesChannelMappings();
    } catch (e) {
      showError(e);
    }
  };

  const restoreInventoryItem = async (id: string) => {
    try {
      setRestoreMappingLoading(true);
      await restoreInventoryItemToSalesChannel(id);
      showSuccess('Item restored successfully.');
      await fetchSalesChannelMappings();
    } catch (e) {
      showError(e);
    } finally {
      setRestoreMappingLoading(false);
    }
  };

  const columns = useMemo(() => [
    {
      name: 'Mapping Id',
      sortable: false,
      selector: 'objectId',
      width: '100px',
    },
    {
      name: 'Sales Channel',
      sortable: true,
      width: '100px',
      cell: (row) => (<CustomLink text={row?.salesChannel?.name} linkTo={`/sales-channels/${row?.salesChannel?.objectId}`} />),
    },
    {
      name: 'Description',
      sortable: false,
      selector: 'salesChannel.description',
    },
    {
      name: 'Deleted',
      sortable: false,
      width: '100px',
      cell: (row) => {
        return (
          <div>
            {row?.isDeleted ? 'true' : 'false'}
          </div>
        );
      },
    },
    {
      name: 'Sync Status',
      sortable: false,
      width: '200px',
      cell: (row) => {
        const canBeSynced = canItemBeSyncedInSalesChannel(row);
        return (
          <div className={canBeSynced ? styles['pending-sync'] : styles['synced']}>
            {canBeSynced ? 'Pending Sync' : 'Synced'}
          </div>
        );
      },
    },
    {
      name: 'Action',
      selector: '',
      sortable: true,
      omit: !isUserAuthorizedToEdit,
      width: '300px',
      // eslint-disable-next-line react/display-name
      cell: (row: any) => (
        <div className={'d-flex'}>
          {row?.isDeleted ? 
            <ActionButton
              text={'Restore'}
              active={!restoreMappingLoading}
              isLoading={restoreMappingLoading}
              onClick={() => restoreInventoryItem(row?.objectId)}
              customClass='mr-2 ml-0'
            /> : 
            <div className='ml-3'>
              <DeleteActionForTable
                onClick={() => deleteInventoryItem(row)}
              />
            </div>
          }
          <SyncItemButton item={row} salesChannelId={row?.salesChannel?.objectId} onSuccess={fetchSalesChannelMappings} />
        </div>
      ),
    },
  ], []);

  return (
    <div className={styles['item-channels-section']}>
      {getHeader()}
      {addSalesChannelModalOpen && <AddItemToSalesChannelModel 
        toggleModal={() => setAddSalesChannelModalOpen(!addSalesChannelModalOpen)} 
        itemId={props?.itemId}
        onSuccess={fetchSalesChannelMappings}
      />}
      <DataTable
        keyField= "objectid"
        columns={columns}
        noHeader={true}
        data={salesChannels}
        highlightOnHover={true}
        defaultSortField="reatedAt"
        customStyles={tableStyles}
        pagination={false}
        loading={loading}
      />
    </div>
  );
};

export default ItemChannels;